/**
 * The Identity Lab's design engine, as pure functions: a shop name plus a shuffle salt
 * becomes a deterministic identity (palette, voice-rosette harmonics, pattern and lockup).
 *
 * No DOM and no p5 in here, so everything is unit-tested (`identity.test.ts`). The sketch
 * (`sketch.ts`) only animates and draws what this module derives.
 *
 * Seed: `mulberry32(hashString(key + salt))`, where `key` is the normalised, lower-cased
 * name, so "sharma  sweets" and "Sharma Sweets" share an identity. Every random draw below
 * happens in a fixed order, so the same name always yields the same identity.
 *
 * @module
 */
import { hashString, mulberry32 } from '@/lib/math'

// ---------------------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------------------

/** Longest name the lab accepts (the input's `maxLength`). */
export const MAX_NAME_LENGTH = 40
/** Each letter adds one harmonic, up to this many; later letters fold into earlier ones. */
export const MAX_HARMONICS = 24
/** Lowest and highest harmonic frequency (whole waves per turn, so every ring closes). */
export const HARMONIC_MIN = 2
export const HARMONIC_MAX = 11
/** Concentric rings in the rosette. Fixed, so any two identities morph point for point. */
export const RING_COUNT = 13

const TAU = Math.PI * 2

// ---------------------------------------------------------------------------------------
// Palettes
// ---------------------------------------------------------------------------------------

/**
 * A curated identity palette. `brand` colours draw the mark (each is at least 3:1 against
 * `paper`); `tints` are light grounds for the pattern band; `ink` sets the lockup (at least
 * 7:1 against `paper`). `identity.test.ts` checks all of it.
 */
export interface IdentityPalette {
  readonly id: string
  readonly paper: string
  readonly ink: string
  readonly brand: readonly [string, string, ...string[]]
  readonly tints: readonly [string, string, ...string[]]
}

/** Twelve palettes, each drawn from a real kind of street: festive, textile, market, tile. */
export const PALETTES: readonly IdentityPalette[] = [
  {
    id: 'saffron',
    paper: '#fbf4e6',
    ink: '#0f1222',
    brand: ['#5b57f2', '#8e1b2e', '#3a33c2'],
    tints: ['#e9ddff', '#d9ecff', '#ffe3ee'],
  },
  {
    id: 'rosewater',
    paper: '#fbf6f3',
    ink: '#0f1222',
    brand: ['#a8344f', '#5b6b3c', '#4a44e0'],
    tints: ['#f2c4cf', '#e9ddff', '#d9e0c8'],
  },
  {
    id: 'indigo-teak',
    paper: '#f5f0e8',
    ink: '#0f1222',
    brand: ['#27357a', '#5b57f2', '#355766'],
    tints: ['#e9ddff', '#c9d3e6', '#d9ecff'],
  },
  {
    id: 'jollof',
    paper: '#fff5ea',
    ink: '#0f1222',
    brand: ['#5b57f2', '#2d6a2e', '#3a33c2'],
    tints: ['#e9ddff', '#fff1d6', '#cfe3b0'],
  },
  {
    id: 'market',
    paper: '#f2f7ee',
    ink: '#0f1222',
    brand: ['#1f6b44', '#4f6a12', '#1f4e5f'],
    tints: ['#bfe3a8', '#fff1d6', '#d7ebc6'],
  },
  {
    id: 'azulejo',
    paper: '#f6f8fc',
    ink: '#0f1222',
    brand: ['#1d4c9e', '#0f2f6e', '#2a64b8'],
    tints: ['#c9daf6', '#fff1d6', '#dde6f7'],
  },
  {
    id: 'rosa',
    paper: '#fff4f8',
    ink: '#0f1222',
    brand: ['#c2006b', '#00704c', '#7a1a55'],
    tints: ['#ffb8d9', '#fff1d6', '#b9ebd6'],
  },
  {
    id: 'iris',
    paper: '#fafafc',
    ink: '#0f1222',
    brand: ['#5b57f2', '#3a33c2', '#2c6a86'],
    tints: ['#e9ddff', '#d9ecff', '#d7f7ec'],
  },
  {
    id: 'terracotta',
    paper: '#f7f1ea',
    ink: '#0f1222',
    brand: ['#5b57f2', '#56653a', '#3a33c2'],
    tints: ['#e9ddff', '#cbd3ae', '#d9ecff'],
  },
  {
    id: 'plum',
    paper: '#f9f4ee',
    ink: '#0f1222',
    brand: ['#6a2c5e', '#2f4f7a', '#4a44e0'],
    tints: ['#fff1d6', '#ffe3ee', '#cad4e6'],
  },
  {
    id: 'lagoon',
    paper: '#eff7f6',
    ink: '#0f1222',
    brand: ['#0d6667', '#b5323b', '#275d8c'],
    tints: ['#fff1d6', '#bfe4e1', '#f4b6b9'],
  },
  {
    id: 'graphite',
    paper: '#f4f4f1',
    ink: '#0f1222',
    brand: ['#2c2f36', '#b42d38', '#35668a'],
    tints: ['#a8d5da', '#e8e4d8', '#f2c9c2'],
  },
]

// ---------------------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------------------

/** One harmonic of the rosette: `k` waves per turn, relative amplitude and phase. */
export interface Harmonic {
  readonly k: number
  readonly amp: number
  readonly phase: number
}

/** `line`: rings stroked on paper. `seal`: a solid brand disc with knocked-out rings. */
export type MarkStyle = 'line' | 'seal'

/** The rosette's shape controls. */
export interface MarkParams {
  readonly style: MarkStyle
  /** Innermost ring radius as a fraction of the outer radius. */
  readonly inner: number
  /** Wave amplitude as a multiple of the ring spacing (above 1, rings weave through each other). */
  readonly depth: number
  /** Rotation in radians from the innermost to the outermost ring: the rosette's twist. */
  readonly twist: number
  /** Relative stroke weight. */
  readonly stroke: number
}

/** Pattern band styles. */
export type PatternKind = 'truchet' | 'halftone' | 'woven'
/** Every pattern, in pick order. */
export const PATTERN_KINDS: readonly PatternKind[] = ['truchet', 'halftone', 'woven']

/** The pattern band. `cells` is the column count across the board. */
export interface PatternParams {
  readonly kind: PatternKind
  readonly cells: number
  readonly seed: number
}

/** CSS `font-stretch` keywords. Canvas 2D takes keywords only (`ctx.fontStretch`). */
export type FontStretch = 'condensed' | 'semi-condensed' | 'normal' | 'semi-expanded' | 'expanded'
/** Each keyword with the `wdth` value it selects on Anek Devanagari (75–125). */
export const FONT_STRETCHES: readonly { readonly keyword: FontStretch; readonly wdth: number }[] = [
  { keyword: 'condensed', wdth: 75 },
  { keyword: 'semi-condensed', wdth: 87.5 },
  { keyword: 'normal', wdth: 100 },
  { keyword: 'semi-expanded', wdth: 112.5 },
  { keyword: 'expanded', wdth: 125 },
]

/**
 * Lockup families. Both are OFL: visitor-generated art never uses Technor, Switzer or Boska
 * (their FFL licence forbids it; see docs/DESIGN.md).
 */
export type LockupFamily = 'anek' | 'mono'
/** CSS family names, exactly as `@fontsource-variable` registers them. */
export const LOCKUP_FONTS: Readonly<Record<LockupFamily, string>> = {
  anek: 'Anek Devanagari Variable',
  mono: 'Geist Mono Variable',
}

/** How the name is set. `tracking` is in em. */
export interface LockupParams {
  readonly family: LockupFamily
  readonly weight: number
  readonly stretch: FontStretch
  readonly upper: boolean
  readonly tracking: number
}

/** The board's colours, picked from one palette. */
export interface IdentityColors {
  readonly paper: string
  readonly ink: string
  /** The brand pick: the mark's colour. */
  readonly brand: string
  /** A second brand colour for the outer rings and swatches. */
  readonly accent: string
  /** The pattern band's ground. */
  readonly tint: string
  /** A second tint, for the woven pattern. */
  readonly tint2: string
}

/** Everything the board shows for one name. */
export interface Identity {
  /** The normalised display name (what the lockup sets). */
  readonly name: string
  readonly salt: number
  readonly seed: number
  readonly paletteId: string
  readonly colors: IdentityColors
  readonly harmonics: readonly Harmonic[]
  readonly mark: MarkParams
  readonly pattern: PatternParams
  readonly lockup: LockupParams
}

/** Trims, collapses whitespace and caps the length. Keeps the visitor's own casing. */
export function normalizeName(raw: string): string {
  return Array.from(raw.replace(/\s+/g, ' ').trim()).slice(0, MAX_NAME_LENGTH).join('').trim()
}

/** The seed for a name and salt: case- and whitespace-insensitive. */
export function seedFor(name: string, salt = 0): number {
  const key = normalizeName(name).toLocaleLowerCase('en')
  return hashString(salt === 0 ? key : `${key}#${salt}`)
}

/** The seed as eight uppercase hex digits, e.g. `3F2A91C4`. */
export function seedHex(seed: number): string {
  return (seed >>> 0).toString(16).toUpperCase().padStart(8, '0')
}

/** Letters and digits of a name (code points, lower-cased); everything else is ignored. */
export function lettersOf(name: string): number[] {
  const letters: number[] = []
  for (const char of name.toLocaleLowerCase('en')) {
    if (/[\p{L}\p{N}]/u.test(char)) letters.push(char.codePointAt(0) ?? 0)
  }
  return letters
}

/**
 * The rosette's harmonics: each letter adds one. A letter's code point picks its frequency,
 * so the same letters always sing the same notes; earlier letters are louder, and higher
 * frequencies are softer so the curves stay smooth. Amplitudes sum to 1.
 */
export function deriveHarmonics(name: string, random: () => number): Harmonic[] {
  const letters = lettersOf(name)
  const source = letters.length > 0 ? letters : [0x76] // "v", for names with no letters
  const span = HARMONIC_MAX - HARMONIC_MIN + 1
  const raw: { k: number; amp: number; phase: number }[] = []

  source.forEach((code, index) => {
    const k = HARMONIC_MIN + (code % span)
    const amp = ((0.6 + 0.4 * random()) / (1 + index * 0.2)) * Math.pow(k, -0.35)
    const phase = random() * TAU
    if (index < MAX_HARMONICS) raw.push({ k, amp, phase })
    else {
      // Past the cap, a letter still adds its voice: it swells an existing harmonic.
      const target = raw[index % MAX_HARMONICS]
      if (target) target.amp += amp * 0.5
    }
  })

  const total = raw.reduce((sum, harmonic) => sum + harmonic.amp, 0) || 1
  return raw.map((harmonic) => ({ ...harmonic, amp: harmonic.amp / total }))
}

/**
 * The rosette's waveform at angle `theta` (radians), rotated by `rotation`. Because the
 * amplitudes sum to 1, the result stays within −1..1.
 */
export function waveAt(harmonics: readonly Harmonic[], theta: number, rotation = 0): number {
  let value = 0
  for (const { k, amp, phase } of harmonics) value += amp * Math.sin(k * (theta + rotation) + phase)
  return value
}

/**
 * Samples every ring's waveform into one flat array (`RING_COUNT × samples`, ring-major).
 * Ring `j` is rotated by `twist · j / (RING_COUNT − 1)`, which twists the rosette.
 */
export function sampleRings(
  harmonics: readonly Harmonic[],
  twist: number,
  samples: number,
  out: Float32Array = new Float32Array(RING_COUNT * samples),
): Float32Array {
  for (let ring = 0; ring < RING_COUNT; ring++) {
    const rotation = (twist * ring) / (RING_COUNT - 1)
    for (let i = 0; i < samples; i++) {
      out[ring * samples + i] = waveAt(harmonics, (i / samples) * TAU, rotation)
    }
  }
  return out
}

function pick<T>(items: readonly T[], random: () => number): T {
  return items[Math.min(items.length - 1, Math.floor(random() * items.length))] as T
}

function range(random: () => number, min: number, max: number): number {
  return min + (max - min) * random()
}

/**
 * Derives the identity for a name. An empty name falls back to `fallbackName`, so the
 * board never goes blank. Same name and salt, same identity, every time.
 */
export function deriveIdentity(rawName: string, salt = 0, fallbackName = 'Vokarta'): Identity {
  const name = normalizeName(rawName) || normalizeName(fallbackName)
  const seed = seedFor(name, salt)
  const random = mulberry32(seed)

  // Palette and the brand pick.
  const palette = pick(PALETTES, random)
  const brandIndex = Math.floor(random() * palette.brand.length) % palette.brand.length
  const brand = palette.brand[brandIndex] as string
  const accent = palette.brand[(brandIndex + 1) % palette.brand.length] as string
  const tintIndex = Math.floor(random() * palette.tints.length) % palette.tints.length
  const tint = palette.tints[tintIndex] as string
  const tint2 = palette.tints[(tintIndex + 1) % palette.tints.length] as string

  // The mark.
  const harmonics = deriveHarmonics(name, random)
  const mark: MarkParams = {
    style: random() < 0.4 ? 'seal' : 'line',
    inner: range(random, 0.16, 0.3),
    depth: range(random, 0.9, 2.4),
    twist: range(random, -1.1, 1.1),
    stroke: range(random, 0.8, 1.3),
  }

  // The pattern band.
  const pattern: PatternParams = {
    kind: pick(PATTERN_KINDS, random),
    cells: 9 + Math.floor(random() * 7),
    seed: Math.floor(random() * 0xffffffff) >>> 0,
  }

  // The lockup: Anek's width and weight axes make most names; a few get Geist Mono.
  const family: LockupFamily = random() < 0.2 ? 'mono' : 'anek'
  const upper = random() < 0.55
  const weight =
    family === 'mono'
      ? 10 * Math.round(range(random, 34, 66))
      : 10 * Math.round(range(random, upper ? 38 : 44, 78))
  const stretch: FontStretch = family === 'mono' ? 'normal' : pick(FONT_STRETCHES, random).keyword
  const heavy = (weight - 340) / 440 // 0 at the lightest, 1 at the heaviest
  // Light capitals open up; heavy ones and lower case stay close.
  const tracking = upper
    ? Number((0.03 + range(random, 0.06, 0.12) * (1 - heavy * 0.7)).toFixed(3))
    : Number(range(random, -0.025, 0.01).toFixed(3))
  const lockup: LockupParams = { family, weight, stretch, upper, tracking }

  return {
    name,
    salt,
    seed,
    paletteId: palette.id,
    colors: { paper: palette.paper, ink: palette.ink, brand, accent, tint, tint2 },
    harmonics,
    mark,
    pattern,
    lockup,
  }
}

// ---------------------------------------------------------------------------------------
// Colour
// ---------------------------------------------------------------------------------------

/** `#rrggbb` → `[r, g, b]` in 0..255. */
export function hexToRgb(hex: string): [number, number, number] {
  const value = Number.parseInt(hex.replace('#', ''), 16)
  return [(value >> 16) & 255, (value >> 8) & 255, value & 255]
}

/** `[r, g, b]` (0..255, clamped and rounded) → `#rrggbb`. */
export function rgbToHex([r, g, b]: readonly [number, number, number]): string {
  const channel = (value: number) =>
    Math.round(Math.min(255, Math.max(0, value)))
      .toString(16)
      .padStart(2, '0')
  return `#${channel(r)}${channel(g)}${channel(b)}`
}

function toLinear(channel: number): number {
  const c = channel / 255
  return c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4)
}

function fromLinear(value: number): number {
  const v = value <= 0.0031308 ? value * 12.92 : 1.055 * Math.pow(value, 1 / 2.4) - 0.055
  return v * 255
}

/** WCAG 2 relative luminance of a hex colour. */
export function luminance(hex: string): number {
  const [r, g, b] = hexToRgb(hex).map(toLinear) as [number, number, number]
  return 0.2126 * r + 0.7152 * g + 0.0722 * b
}

/** WCAG 2 contrast ratio between two hex colours (1..21). */
export function contrastRatio(a: string, b: string): number {
  const [light, dark] = [luminance(a), luminance(b)].sort((x, y) => y - x) as [number, number]
  return (light + 0.05) / (dark + 0.05)
}

/** An OKLab colour: `[L, a, b]`. Interpolating here keeps morphs perceptually even. */
export type Oklab = [number, number, number]

/** `#rrggbb` → OKLab. */
export function hexToOklab(hex: string): Oklab {
  const [r, g, b] = hexToRgb(hex).map(toLinear) as [number, number, number]
  const l = Math.cbrt(0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b)
  const m = Math.cbrt(0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b)
  const s = Math.cbrt(0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b)
  return [
    0.2104542553 * l + 0.793617785 * m - 0.0040720468 * s,
    1.9779984951 * l - 2.428592205 * m + 0.4505937099 * s,
    0.0259040371 * l + 0.7827717662 * m - 0.808675766 * s,
  ]
}

/** OKLab → `[r, g, b]` in 0..255 (unclamped). */
export function oklabToRgb([L, A, B]: readonly [number, number, number]): [number, number, number] {
  const l = (L + 0.3963377774 * A + 0.2158037573 * B) ** 3
  const m = (L - 0.1055613458 * A - 0.0638541728 * B) ** 3
  const s = (L - 0.0894841775 * A - 1.291485548 * B) ** 3
  return [
    fromLinear(4.0767416621 * l - 3.3077115913 * m + 0.2309699292 * s),
    fromLinear(-1.2684380046 * l + 2.6097574011 * m - 0.3413193965 * s),
    fromLinear(-0.0041960863 * l - 0.7034186147 * m + 1.707614701 * s),
  ]
}

/** Mixes two hex colours in OKLab (`t = 0` gives `a`, `t = 1` gives `b`). */
export function mixOklab(a: string, b: string, t: number): string {
  const x = hexToOklab(a)
  const y = hexToOklab(b)
  return rgbToHex(
    oklabToRgb([x[0] + (y[0] - x[0]) * t, x[1] + (y[1] - x[1]) * t, x[2] + (y[2] - x[2]) * t]),
  )
}

// ---------------------------------------------------------------------------------------
// Files
// ---------------------------------------------------------------------------------------

/**
 * A filename-safe slug that keeps every script: Latin loses its accents, Devanagari keeps
 * its vowel signs. "Mama Nkechi’s Kitchen" → `mama-nkechis-kitchen`, "शर्मा स्वीट्स" →
 * `शर्मा-स्वीट्स`. Falls back to `fallback` when nothing is left.
 */
export function slugify(name: string, fallback = 'identity'): string {
  const slug = name
    .normalize('NFKD')
    .replace(/(\p{Script=Latin})\p{M}+/gu, '$1')
    .toLocaleLowerCase('en')
    .replace(/['’‘`]/g, '')
    .match(/[\p{L}\p{M}\p{N}]+/gu)
    ?.join('-')
    .normalize('NFC')
    .slice(0, 60)
    .replace(/-+$/, '')
  return slug || fallback
}
